import { useState } from 'react';
import { X } from 'lucide-react';

interface CreateFulfillmentCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (cardData: any) => void;
}

interface CardFormData {
  cardType: string;
  cardNumber: string;
  expiryDate: string;
  cvvNumber: string;
}

export default function CreateFulfillmentCardModal({ isOpen, onClose, onSubmit }: CreateFulfillmentCardModalProps) {
  const [formData, setFormData] = useState<CardFormData>({
    cardType: '',
    cardNumber: '',
    expiryDate: '',
    cvvNumber: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: keyof CardFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await onSubmit(formData);
      onClose();
      // Reset form
      setFormData({
        cardType: '',
        cardNumber: '',
        expiryDate: '',
        cvvNumber: ''
      });
    } catch (error) {
      console.error('Error creating card:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className={`fixed top-0 bg-white shadow-2xl transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          width: '980px',
          height: '100vh',
          right: '0px',
          top: '0px'
        }}
      >
        {/* Header */}
        <div className="bg-secondary-500 text-white p-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-[40px] leading-none font-medium">Create Card</h2>
              <p className="text-[24px] text-white/80 mt-2">Fill details to create card</p>
            </div>
            <button 
              onClick={onClose}
              className="bg-[#426198] px-2 py-2 rounded-full text-white hover:text-gray-300 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto" style={{ height: 'calc(100vh - 120px)' }}>
          <form onSubmit={handleSubmit} className="p-6">
            {/* Two Column Layout */}
            <div className="grid grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                <div>
                  <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                    Card Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.cardType}
                    onChange={(e) => handleInputChange('cardType', e.target.value)}
                    required
                    className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                  >
                    <option value="">Select</option>
                    <option value="Amazone">Amazone</option>
                    <option value="Visa">Visa</option>
                    <option value="Mastercard">Mastercard</option>
                    <option value="American Express">American Express</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                    Expire Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.expiryDate}
                    onChange={(e) => handleInputChange('expiryDate', e.target.value)}
                    required
                    className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                    placeholder="MM/YY"
                  />
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <div>
                  <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                    Card Number <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.cardNumber}
                    onChange={(e) => handleInputChange('cardNumber', e.target.value)}
                    required
                    className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                  >
                    <option value="">Select</option>
                    <option value="5574">5574</option>
                    <option value="1234">1234</option>
                    <option value="5678">5678</option>
                    <option value="9012">9012</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                    CVV Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.cvvNumber}
                    onChange={(e) => handleInputChange('cvvNumber', e.target.value)}
                    required
                    className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                    placeholder="Enter CVV"
                    maxLength={4}
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-8">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-lg bg-[#F6C600] py-3 px-6 text-[18px] font-medium text-black transition-colors hover:bg-[#e1b500] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Creating...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
